import { usePostHog } from 'posthog-js/react';
import { useMemo } from 'react';

import {
    type FilterChip,
    type FilterDimension,
    type FilterOperator,
} from '@apps/web/components/ui/filter_bar';
import { EVENTS } from '@apps/web/lib/events';

/**
 * Product analytics for Conversations v0 (the redesigned inbox at
 * /conversations).
 *
 * One hook owns the payload contract so handlers in the inbox route cannot
 * describe the same interaction two ways. Every capture is best-effort:
 * analytics must never break inbox interactions.
 */

export const CONVERSATIONS_INBOX_V0_SURFACE = 'conversations_v0';

export type ConversationsInboxFilterType =
    | 'unread'
    | 'needs_review'
    | 'office'
    | 'assignee'
    | 'caregiver_tags'
    | 'sort';

export type ConversationsInboxFilterAction =
    | 'add'
    | 'remove'
    | 'include'
    | 'exclude'
    | 'set'
    | 'clear'
    | 'toggle_on'
    | 'toggle_off'
    | 'change_operator';

export type ConversationsInboxFilterOperator = 'is' | 'is_not';

export type ConversationsInboxBulkAction =
    | 'mark_read'
    | 'mark_unread'
    | 'assign'
    | 'mark_all_read';

export type ConversationsInboxThreadOpenedFrom = 'list' | 'auto';

export type ConversationsInboxFilterChangedEvent = {
    readonly filterType: ConversationsInboxFilterType;
    readonly action: ConversationsInboxFilterAction;
    readonly value?: string;
    readonly operator?: ConversationsInboxFilterOperator;
    readonly filterMode?: 'include' | 'exclude';
    readonly activeFilterTypes?: ReadonlyArray<ConversationsInboxFilterType>;
    readonly resultCount?: number;
};

export type ConversationsInboxFiltersClearedEvent = {
    readonly clearedFilterCount: number;
    readonly hadSearch: boolean;
};

export type ConversationsInboxSearchUsedEvent = {
    readonly queryLength: number;
    readonly resultCount: number;
    readonly hadActiveFilters: boolean;
};

export type ConversationsInboxViewedEvent = {
    readonly unreadCount: number | null;
    readonly conversationCount: number;
};

export type ConversationsInboxThreadOpenedEvent = {
    readonly conversationId: string;
    readonly hadUnread: boolean;
    readonly hadPendingReview: boolean;
    readonly openedFrom: ConversationsInboxThreadOpenedFrom;
    readonly activeFilterTypes: ReadonlyArray<ConversationsInboxFilterType>;
};

export type ConversationsInboxShiftOverlayOpenedEvent = {
    readonly conversationId: string;
    readonly shiftId: string;
};

export type ConversationsInboxBulkActionEvent = {
    readonly action: ConversationsInboxBulkAction;
    readonly selectedCount: number;
};

export type ConversationsInboxAssignedEvent = {
    readonly conversationId: string;
    readonly assignedToUserId: string | null;
};

export type ConversationsInboxFilterSnapshot = {
    readonly unreadOnly: boolean;
    readonly needsReviewOnly: boolean;
    readonly chips: ReadonlyArray<FilterChip>;
    readonly sort: string;
};

export type ConversationsInboxAnalytics = {
    readonly captureInboxViewed: (event: ConversationsInboxViewedEvent) => void;
    readonly captureFilterChanged: (
        event: ConversationsInboxFilterChangedEvent,
    ) => void;
    readonly captureFiltersCleared: (
        event: ConversationsInboxFiltersClearedEvent,
    ) => void;
    readonly captureSearchUsed: (
        event: ConversationsInboxSearchUsedEvent,
    ) => void;
    readonly captureThreadOpened: (
        event: ConversationsInboxThreadOpenedEvent,
    ) => void;
    readonly captureShiftOverlayOpened: (
        event: ConversationsInboxShiftOverlayOpenedEvent,
    ) => void;
    readonly captureBulkAction: (
        event: ConversationsInboxBulkActionEvent,
    ) => void;
    readonly captureAssigned: (event: ConversationsInboxAssignedEvent) => void;
};

const CHIP_FILTER_TYPES = new Set<ConversationsInboxFilterType>([
    'office',
    'assignee',
    'caregiver_tags',
]);

function isInboxFilterType(
    value: string,
): value is ConversationsInboxFilterType {
    return (
        value === 'unread' ||
        value === 'needs_review' ||
        value === 'office' ||
        value === 'assignee' ||
        value === 'caregiver_tags' ||
        value === 'sort'
    );
}

function analyticsOperator(
    operator: FilterOperator | undefined,
): ConversationsInboxFilterOperator {
    return operator === 'is_not' ? 'is_not' : 'is';
}

function filterModeForOperator(
    operator: ConversationsInboxFilterOperator,
): 'include' | 'exclude' {
    return operator === 'is_not' ? 'exclude' : 'include';
}

function chipHasValues(
    chips: ReadonlyArray<FilterChip>,
    dimension: string,
): boolean {
    return chips.some(
        (chip) => chip.dimension === dimension && chip.values.length > 0,
    );
}

export function conversationInboxActiveFilterTypes(
    snapshot: ConversationsInboxFilterSnapshot,
): Array<ConversationsInboxFilterType> {
    const types: Array<ConversationsInboxFilterType> = [];
    if (snapshot.unreadOnly) {
        types.push('unread');
    }
    if (snapshot.needsReviewOnly) {
        types.push('needs_review');
    }
    if (chipHasValues(snapshot.chips, 'office')) {
        types.push('office');
    }
    if (chipHasValues(snapshot.chips, 'assignee')) {
        types.push('assignee');
    }
    if (chipHasValues(snapshot.chips, 'caregiver_tags')) {
        types.push('caregiver_tags');
    }
    // Newest is the default ordering, so any other choice counts as an active
    // sort in combo snapshots.
    if (snapshot.sort !== 'newest') {
        types.push('sort');
    }
    return types;
}

function labelForChipValue(
    dimensions: ReadonlyArray<FilterDimension>,
    dimension: string,
    value: string,
): string {
    const options = dimensions.find(
        (candidate) => candidate.key === dimension,
    )?.options;
    return options?.find((option) => option.value === value)?.label ?? value;
}

type FlatChipValue = {
    readonly dimension: ConversationsInboxFilterType;
    readonly operator: ConversationsInboxFilterOperator;
    readonly value: string;
};

function flattenChipValues(
    chips: ReadonlyArray<FilterChip>,
): Array<FlatChipValue> {
    const flattened: Array<FlatChipValue> = [];
    for (const chip of chips) {
        if (!isInboxFilterType(chip.dimension)) {
            continue;
        }
        if (!CHIP_FILTER_TYPES.has(chip.dimension)) {
            continue;
        }
        const operator = analyticsOperator(chip.operator);
        for (const value of chip.values) {
            flattened.push({
                dimension: chip.dimension,
                operator,
                value,
            });
        }
    }
    return flattened;
}

function flatKey(entry: FlatChipValue): string {
    return `${entry.dimension}:${entry.operator}:${entry.value}`;
}

function chipEventFields(
    dimension: ConversationsInboxFilterType,
    operator: ConversationsInboxFilterOperator,
    value: string | undefined,
    action: ConversationsInboxFilterAction,
): ConversationsInboxFilterChangedEvent {
    const isTag = dimension === 'caregiver_tags';
    return {
        filterType: dimension,
        action,
        ...(value !== undefined ? { value } : {}),
        ...(isTag
            ? { operator, filterMode: filterModeForOperator(operator) }
            : {}),
    };
}

/**
 * Diff FilterBar chips into the inbox filter-changed contract: add/remove per
 * value, and a single change_operator when values move between is / is_not.
 */
export function describeConversationInboxChipChanges(
    previous: ReadonlyArray<FilterChip>,
    next: ReadonlyArray<FilterChip>,
    dimensions: ReadonlyArray<FilterDimension>,
): Array<ConversationsInboxFilterChangedEvent> {
    const previousEntries = flattenChipValues(previous);
    const nextEntries = flattenChipValues(next);
    const previousByKey = new Map(
        previousEntries.map((entry) => [flatKey(entry), entry]),
    );
    const nextByKey = new Map(
        nextEntries.map((entry) => [flatKey(entry), entry]),
    );

    const unpairedRemoved: Array<FlatChipValue> = [];
    const unpairedAdded: Array<FlatChipValue> = [];
    const operatorMoves = new Map<
        ConversationsInboxFilterType,
        ConversationsInboxFilterOperator
    >();

    for (const entry of previousEntries) {
        if (!nextByKey.has(flatKey(entry))) {
            unpairedRemoved.push(entry);
        }
    }
    for (const entry of nextEntries) {
        if (!previousByKey.has(flatKey(entry))) {
            unpairedAdded.push(entry);
        }
    }

    const stillRemoved: Array<FlatChipValue> = [];
    const consumedAdded = new Set<string>();

    for (const removed of unpairedRemoved) {
        const moved = unpairedAdded.find(
            (added) =>
                added.dimension === removed.dimension &&
                added.value === removed.value &&
                added.operator !== removed.operator &&
                !consumedAdded.has(flatKey(added)),
        );
        if (moved) {
            consumedAdded.add(flatKey(moved));
            operatorMoves.set(removed.dimension, moved.operator);
            continue;
        }
        stillRemoved.push(removed);
    }

    const events: Array<ConversationsInboxFilterChangedEvent> = [];
    for (const [dimension, operator] of operatorMoves) {
        events.push(
            chipEventFields(dimension, operator, undefined, 'change_operator'),
        );
    }
    for (const added of unpairedAdded) {
        if (consumedAdded.has(flatKey(added))) {
            continue;
        }
        events.push(
            chipEventFields(
                added.dimension,
                added.operator,
                labelForChipValue(dimensions, added.dimension, added.value),
                'add',
            ),
        );
    }
    for (const removed of stillRemoved) {
        events.push(
            chipEventFields(
                removed.dimension,
                removed.operator,
                labelForChipValue(dimensions, removed.dimension, removed.value),
                'remove',
            ),
        );
    }
    return events;
}

export function useConversationsInboxAnalytics({
    organizationId,
}: {
    readonly organizationId: string | null;
}): ConversationsInboxAnalytics {
    const posthog = usePostHog();

    return useMemo<ConversationsInboxAnalytics>(() => {
        function capture(
            eventName: string,
            properties: Record<string, unknown>,
        ): void {
            if (organizationId === null) {
                return;
            }
            try {
                posthog?.capture(eventName, {
                    organization_id: organizationId,
                    surface: CONVERSATIONS_INBOX_V0_SURFACE,
                    ...properties,
                });
            } catch {
                // Best-effort: analytics must not break inbox interactions.
            }
        }

        return {
            captureInboxViewed: (event) => {
                capture(EVENTS.CONVERSATION_INBOX_VIEWED, {
                    unread_count: event.unreadCount,
                    conversation_count: event.conversationCount,
                });
            },
            captureFilterChanged: (event) => {
                capture(EVENTS.CONVERSATION_INBOX_FILTER_CHANGED, {
                    filter_type: event.filterType,
                    action: event.action,
                    ...(event.value !== undefined
                        ? { value: event.value }
                        : {}),
                    ...(event.operator !== undefined
                        ? { operator: event.operator }
                        : {}),
                    ...(event.filterMode !== undefined
                        ? { filter_mode: event.filterMode }
                        : {}),
                    ...(event.activeFilterTypes !== undefined
                        ? { active_filter_types: event.activeFilterTypes }
                        : {}),
                    ...(event.resultCount !== undefined
                        ? { result_count: event.resultCount }
                        : {}),
                });
            },
            captureFiltersCleared: (event) => {
                capture(EVENTS.CONVERSATION_INBOX_FILTERS_CLEARED, {
                    cleared_filter_count: event.clearedFilterCount,
                    had_search: event.hadSearch,
                });
            },
            captureSearchUsed: (event) => {
                capture(EVENTS.CONVERSATION_INBOX_SEARCH_USED, {
                    query_length: event.queryLength,
                    result_count: event.resultCount,
                    had_active_filters: event.hadActiveFilters,
                });
            },
            captureThreadOpened: (event) => {
                capture(EVENTS.CONVERSATION_THREAD_OPENED, {
                    conversation_id: event.conversationId,
                    had_unread: event.hadUnread,
                    had_pending_review: event.hadPendingReview,
                    opened_from: event.openedFrom,
                    active_filter_types: event.activeFilterTypes,
                });
            },
            captureShiftOverlayOpened: (event) => {
                capture(EVENTS.CONVERSATION_SHIFT_OVERLAY_OPENED, {
                    conversation_id: event.conversationId,
                    shift_id: event.shiftId,
                    source: 'footnote',
                });
            },
            captureBulkAction: (event) => {
                capture(EVENTS.CONVERSATION_INBOX_BULK_ACTION, {
                    action: event.action,
                    selected_count: event.selectedCount,
                });
            },
            captureAssigned: (event) => {
                capture(EVENTS.CONVERSATION_ASSIGNED, {
                    conversation_id: event.conversationId,
                    assigned_to_user_id: event.assignedToUserId,
                    source: 'detail_pane',
                });
            },
        };
    }, [organizationId, posthog]);
}
